import React, { useState, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert,
  Dimensions,
  Animated
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Card,
  Title,
  Paragraph,
  Chip,
  Surface
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { getUserProfile, generateCategoryMatches } from '../../utils/compatibility';
import { getLifePathDisplayValue, shouldShowAlternateLifePath } from '../../utils/numerology';
import { 
  colors, 
  spacing, 
  borderRadius, 
  shadows, 
  typography,
  commonStyles,
  getCardMaxWidth,
  getPadding 
} from './sharedStyles';

export default function MatchCalculatorComponent() {
  const [birthDateString, setBirthDateString] = useState('');
  const [matches, setMatches] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Refs for autoscroll functionality
  const scrollViewRef = useRef(null);
  const profileRef = useRef(null);
  // Ref for birth date input
  const birthDateInputRef = useRef(null);

  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const isTablet = width >= 768 && width < 1024;
  const isMobile = width < 768;

  const formatBirthDate = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  const handleBirthDateChange = (text) => {
    const formatted = formatBirthDate(text);
    setBirthDateString(formatted);
  };

  const renderLifePathCombinationBreakdown = (lifePathData) => {
    const combinations = lifePathData?.combinations || [];
    const birthdayComposite = lifePathData?.birthdayComposite;
    if (!combinations.length && !birthdayComposite) return null;

    return (
      <View style={styles.lifePathBreakdown}>
        {combinations.map((combination) => (
          <View key={combination.key} style={styles.lifePathBreakdownItem}>
            <Text style={styles.lifePathBreakdownLabel}>
              {combination.label}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Classical {combination.classical.reduction}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Reduced {combination.reduced.reduction}
            </Text>
          </View>
        ))}
        {birthdayComposite && (
          <View style={styles.lifePathComposite}>
            <Text style={styles.lifePathBreakdownLabel}>
              {birthdayComposite.label}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Month + Day {birthdayComposite.monthDay.reduction}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Day + Year {birthdayComposite.dayYear.reduction}
            </Text>
            <Text style={styles.lifePathCompositeValue}>
              Total {birthdayComposite.total.reduction}
            </Text>
          </View>
        )}
      </View>
    );
  };

  // Removed handleBirthDateFocus - using selectTextOnFocus prop instead

  const handleFindMatches = () => {
    if (!birthDateString || birthDateString.length < 10) {
      Alert.alert('Error', 'Please enter a valid birth date');
      return;
    }

    const [month, day, year] = birthDateString.split('-');
    const isoDateString = `${year}-${month}-${day}`;
    const profile = getUserProfile('', isoDateString);
    const categoryMatches = generateCategoryMatches(profile);
    
    setUserProfile(profile);
    setMatches(categoryMatches);

    // Auto-scroll to profile section on mobile after results populate
    if (isMobile) {
      setTimeout(() => {
        if (profileRef.current && scrollViewRef.current) {
          profileRef.current.measureLayout(
            scrollViewRef.current,
            (x, y) => {
              scrollViewRef.current.scrollTo({
                y: y - 20, // Small offset for better visual positioning
                animated: true,
              });
            },
            () => {
              // Fallback if measureLayout fails - scroll by estimated position
              scrollViewRef.current.scrollTo({
                y: 400,
                animated: true,
              });
            }
          );
        }
      }, 300); // Longer delay to ensure results are fully rendered
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return colors.success;
    if (score >= 65) return colors.warning;
    return colors.error;
  };

  return (
    <View style={commonStyles.container}>
      <ScrollView ref={scrollViewRef} style={styles.scrollView}>
        <View style={styles.mainContent}>
          {/* Input Section */}
          <View style={[
            styles.inputContainer,
            { maxWidth: getCardMaxWidth(width) }
          ]}>
            <Surface style={[commonStyles.solidCard, styles.inputCard]}>
              <View style={styles.inputHeader}>
                <View style={styles.iconContainer}>
                  <Ionicons name="heart" size={28} color={colors.primary} />
                </View>
                <Title style={commonStyles.cardTitle}>
                  Find Your Perfect Match
                </Title>
                <Paragraph style={styles.inputSubtitle}>
                  Enter your birth date to discover your ideal compatibility matches
                </Paragraph>
              </View>

              <View style={styles.inputFieldContainer}>
                <View style={styles.fieldWrapper}>
                  <Text style={styles.fieldLabel}>Birth Date</Text>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar-outline" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      ref={birthDateInputRef}
                      value={birthDateString}
                      onChangeText={handleBirthDateChange}
                      onSubmitEditing={handleFindMatches}
                      selectTextOnFocus={true}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="MM-DD-YYYY"
                      keyboardType="numeric"
                      maxLength={10}
                      returnKeyType="done"
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '40',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </View>

                <LinearGradient
                  colors={[colors.primary, colors.primaryLight]}
                  style={styles.gradientButton}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  <Button
                    mode="contained"
                    onPress={handleFindMatches}
                    style={styles.calculateButton}
                    labelStyle={styles.calculateButtonText}
                    contentStyle={styles.calculateButtonContent}
                    buttonColor="transparent"
                  >
                    <Ionicons name="search" size={18} color={colors.white} style={styles.buttonIcon} />
                    Find My Matches
                  </Button>
                </LinearGradient>
              </View>
            </Surface>
          </View>

          {/* Profile Display */}
          {userProfile && (
            <Surface ref={profileRef} style={[commonStyles.resultCard, styles.profileCard]}>
              <View style={styles.profileHeader}>
                <LinearGradient
                  colors={['rgba(116, 75, 162, 0.1)', 'rgba(102, 126, 234, 0.1)']}
                  style={styles.profileIconContainer}
                >
                  <MaterialCommunityIcons name="star-four-points" size={32} color={colors.primary} />
                </LinearGradient>
                <Title style={styles.profileTitle}>Your Cosmic Profile</Title>
                <Text style={styles.profileSubtitle}>
                  Based on your birth date analysis
                </Text>
              </View>

              <View style={styles.profileChips}>
                <View style={styles.chipRow}>
                  <Surface style={[styles.modernChip, styles.chineseChip]}>
                    <View style={styles.chipContent}>
                      <Text style={styles.chipEmoji}>🐉</Text>
                      <Text style={styles.chipLabel}>Chinese Zodiac</Text>
                      <Text style={styles.chipValue}>
                        {userProfile.chineseElement} {userProfile.chineseAnimal}
                      </Text>
                    </View>
                  </Surface>

                  <Surface style={[styles.modernChip, styles.westernChip]}>
                    <View style={styles.chipContent}>
                      <Text style={styles.chipEmoji}>⭐</Text>
                      <Text style={styles.chipLabel}>Western Sign</Text>
                      <Text style={styles.chipValue}>
                        {userProfile.westernSign.name}
                      </Text>
                    </View>
                  </Surface>
                </View>

                <Surface style={[styles.modernChip, styles.numerologyChip, styles.fullWidthChip, [11, 22, 28, 33].includes(userProfile.lifePath) && styles.masterNumberChip]}>
                  <View style={[styles.chipContent, styles.centeredChipContent]}>
                    <Text style={styles.chipEmoji}>🔢</Text>
                    <Text style={styles.chipLabel}>Life Path Number</Text>
                    <Text style={[styles.chipValue, styles.largeChipValue, [11, 22, 28, 33].includes(userProfile.lifePath) && styles.masterNumberValue]}>
                      {getLifePathDisplayValue(userProfile.lifePathData, userProfile.lifePath)}
                    </Text>
                    {[11, 22, 28, 33].includes(userProfile.lifePath) && (
                      <Text style={styles.masterNumberLabel}>
                        {userProfile.lifePathData && userProfile.lifePathData.isPure ? 'Pure Master Number' : 'Master Number'}
                        {userProfile.lifePathData && !userProfile.lifePathData.isPure && userProfile.lifePathData.reduction.includes('/') &&
                          ` (${userProfile.lifePathData.reduction.split('/').slice(-1)[0]})`
                        }
                      </Text>
                    )}
                    {shouldShowAlternateLifePath(userProfile.lifePathData) && (
                      <Text style={styles.masterNumberLabel}>
                        Alt method: {getLifePathDisplayValue(userProfile.lifePathData.alternate)}
                      </Text>
                    )}
                    {renderLifePathCombinationBreakdown(userProfile.lifePathData)}
                  </View>
                </Surface>
              </View>
            </Surface>
          )}

          {/* Results Display */}
          {matches && (
            <View style={styles.resultsContainer}>
              <Title style={styles.resultsTitle}>Your Best Matches</Title>
              
              {/* Chinese Astrology Matches */}
              <Surface style={styles.categoryCard}>
                <View style={styles.categoryHeader}>
                  <Title style={styles.categoryTitle}>🐉 Chinese Astrology</Title>
                  <Paragraph style={styles.categoryDescription}>
                    Birth year energy compatibility
                  </Paragraph>
                </View>
                <View style={styles.matchesGrid}>
                  {matches.chinese.slice(0, 6).map((match, index) => (
                    <Surface key={index} style={styles.matchCard}>
                      <View style={styles.matchHeader}>
                        <Text style={styles.matchTitle}>{match.animal}</Text>
                        <View style={styles.scoreContainer}>
                          <Surface style={[styles.scoreChip, { backgroundColor: getScoreColor(match.score) }]}>
                            <Text style={styles.scoreText}>{match.score}%</Text>
                          </Surface>
                        </View>
                      </View>
                      <Text style={styles.matchDescription}>
                        {match.description.split('\n')[0]}
                      </Text>
                    </Surface>
                  ))}
                </View>
              </Surface>

              {/* Western Astrology Matches */}
              <Surface style={styles.categoryCard}>
                <View style={styles.categoryHeader}>
                  <Title style={styles.categoryTitle}>⭐ Western Astrology</Title>
                  <Paragraph style={styles.categoryDescription}>
                    Birth month energy compatibility
                  </Paragraph>
                </View>
                <View style={styles.matchesGrid}>
                  {matches.western.slice(0, 6).map((match, index) => (
                    <Surface key={index} style={styles.matchCard}>
                      <View style={styles.matchHeader}>
                        <Text style={styles.matchTitle}>
                          {match.sign} ({match.element})
                        </Text>
                        <View style={styles.scoreContainer}>
                          <Surface style={[styles.scoreChip, { backgroundColor: getScoreColor(match.score) }]}>
                            <Text style={styles.scoreText}>{match.score}%</Text>
                          </Surface>
                        </View>
                      </View>
                      <Text style={styles.matchDescription}>
                        {match.description.split('\n')[0]}
                      </Text>
                    </Surface>
                  ))}
                </View>
              </Surface>

              {/* Numerology Matches */}
              <Surface style={styles.categoryCard}>
                <View style={styles.categoryHeader}>
                  <Title style={styles.categoryTitle}>🔢 Numerology</Title>
                  <Paragraph style={styles.categoryDescription}>
                    Life Path energy compatibility
                  </Paragraph>
                </View>
                <View style={styles.matchesGrid}>
                  {matches.numerology.slice(0, 6).map((match, index) => (
                    <Surface key={index} style={[styles.matchCard, [11, 22, 28, 33].includes(match.lifePath) && styles.masterNumberMatchCard]}>
                      <View style={styles.matchHeader}>
                        <View style={styles.matchTitleContainer}>
                          <Text style={[styles.matchTitle, [11, 22, 28, 33].includes(match.lifePath) && styles.masterNumberMatchTitle]}>
                            Life Path {match.lifePath === 28 ? '(28)1' : match.lifePath}
                          </Text>
                          {[11, 22, 28, 33].includes(match.lifePath) && (
                            <Text style={styles.masterNumberBadge}>MASTER</Text>
                          )}
                        </View>
                        <View style={styles.scoreContainer}>
                          <Surface style={[styles.scoreChip, { backgroundColor: getScoreColor(match.score) }]}>
                            <Text style={styles.scoreText}>{match.score}%</Text>
                          </Surface>
                        </View>
                      </View>
                      <Text style={styles.matchDescription}>
                        {match.description.split('\n')[0]}
                      </Text>
                    </Surface>
                  ))}
                </View>
              </Surface>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: spacing.lg,
  },
  mainContent: {
    alignItems: 'center',
    paddingBottom: spacing.xxl,
  },
  
  // Input Section
  inputContainer: {
    width: '100%',
    alignSelf: 'center',
    marginBottom: spacing.xxl,
  },
  inputCard: {
    padding: spacing.xxl,
  },
  inputHeader: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: `${colors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  inputSubtitle: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  inputFieldContainer: {
    gap: spacing.xl,
  },
  fieldWrapper: {
    gap: spacing.sm,
  },
  fieldLabel: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
    marginLeft: spacing.sm,
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 1,
  },
  modernInput: {
    flex: 1,
    backgroundColor: colors.white,
    paddingLeft: spacing.xxxl + spacing.sm,
    ...shadows.input,
  },
  gradientButton: {
    borderRadius: borderRadius.xl,
    ...shadows.button,
  },
  calculateButton: {
    backgroundColor: 'transparent',
    elevation: 0,
    shadowOpacity: 0,
  },
  calculateButtonContent: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  calculateButtonText: {
    ...typography.button,
    color: colors.white,
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },

  // Profile Section
  profileCard: {
    width: '100%',
    maxWidth: getCardMaxWidth(800),
    alignSelf: 'center',
    marginBottom: spacing.xxl,
  },
  profileHeader: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  profileIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  profileTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  profileSubtitle: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  profileChips: {
    gap: spacing.lg,
  },
  chipRow: {
    flexDirection: 'row',
    gap: spacing.lg,
  },
  modernChip: {
    flex: 1,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.white,
    ...shadows.input,
  },
  fullWidthChip: {
    flex: undefined,
    width: '100%',
  },
  chipContent: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  centeredChipContent: {
    alignItems: 'center',
  },
  chipEmoji: {
    fontSize: 24,
    marginBottom: spacing.xs,
  },
  chipLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
    textAlign: 'center',
  },
  chipValue: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '700',
    textAlign: 'center',
  },
  largeChipValue: {
    ...typography.heading3,
    color: colors.primary,
  },
  chineseChip: {
    borderLeftWidth: 4,
    borderLeftColor: colors.warning,
  },
  westernChip: {
    borderLeftWidth: 4,
    borderLeftColor: colors.info,
  },
  numerologyChip: {
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
  },
  masterNumberChip: {
    borderLeftColor: '#FFD700',
    backgroundColor: 'rgba(255, 215, 0, 0.05)',
    borderWidth: 2,
    borderColor: '#FFD700',
  },
  masterNumberValue: {
    color: '#FF8C00',
  },
  masterNumberLabel: {
    ...typography.caption,
    color: '#FF8C00',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: spacing.xs,
  },
  lifePathBreakdown: {
    width: '100%',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 175, 80, 0.22)',
    gap: spacing.xs,
  },
  lifePathBreakdownItem: {
    width: '100%',
    gap: 2,
  },
  lifePathComposite: {
    width: '100%',
    gap: 2,
    marginTop: spacing.xs,
    paddingTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 175, 80, 0.16)',
  },
  lifePathBreakdownLabel: {
    ...typography.caption,
    color: colors.textPrimary,
    fontWeight: '700',
    textAlign: 'center',
  },
  lifePathBreakdownValue: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
    textAlign: 'center',
  },
  lifePathCompositeValue: {
    ...typography.caption,
    color: '#FF8C00',
    fontWeight: '700',
    textAlign: 'center',
  },

  // Results Section
  resultsContainer: {
    width: '100%',
    maxWidth: 1000,
    alignSelf: 'center',
  },
  resultsTitle: {
    ...typography.heading1,
    color: colors.white,
    textAlign: 'center',
    marginBottom: spacing.xxl,
  },
  categoryCard: {
    ...commonStyles.resultCard,
    marginBottom: spacing.xxl,
  },
  categoryHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  categoryTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  categoryDescription: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  matchesGrid: {
    gap: spacing.lg,
  },
  matchCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...shadows.input,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary + '40',
  },
  matchHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  matchTitleContainer: {
    flex: 1,
    marginRight: spacing.md,
  },
  matchTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  masterNumberMatchCard: {
    borderLeftColor: '#FFD700',
    backgroundColor: 'rgba(255, 215, 0, 0.02)',
    borderWidth: 1,
    borderColor: '#FFD700',
  },
  masterNumberMatchTitle: {
    color: '#FF8C00',
  },
  masterNumberBadge: {
    ...typography.caption,
    color: '#FF8C00',
    fontWeight: '800',
    fontSize: 10,
    letterSpacing: 0.5,
    backgroundColor: 'rgba(255, 140, 0, 0.1)',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  scoreContainer: {
    alignItems: 'center',
    minWidth: 60,
  },
  scoreChip: {
    borderRadius: borderRadius.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    ...shadows.input,
  },
  scoreText: {
    ...typography.bodySmall,
    color: colors.white,
    fontWeight: '700',
  },
  matchDescription: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    lineHeight: 20,
  },
});
